import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  AuthorizationService,
  RobotEscalationService,
  User,
  UserPreference,
  UserService
} from '@ng-cloud/badger-core';
import type { IdleRobotEscalation } from '@ng-cloud/badger-core/models/idle-robot-escalation';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { RobotEscalationIdleDialogComponent } from './robot-escalation-idle-dialog';

@Injectable({
  providedIn: 'root'
})
export class RobotEscalationIdleAlertService implements OnDestroy {
  private static readonly POLL_INTERVAL_MS = 60000;
  /** Seed for the dialog heading only, until the first idle_count response reports the real value. */
  private static readonly DEFAULT_IDLE_MINUTES = 20;
  private static readonly PREF_AUTO_POPUP_DISABLED = 'robot_escalation_auto_popup_disabled';
  private static readonly PREF_LAST_DIALOG_OPENED_AT = 'robot_escalation_idle_dialog_last_opened_at';

  private readonly subscriptions = new Subscription();
  private poller: Subscription;
  private dialogRef: MatDialogRef<RobotEscalationIdleDialogComponent>;

  private initialized = false;
  private preferenceLoaded = false;
  private preferenceUserId: number = null;
  private isSupervisor = false;
  private currentUser: User;
  private userPreference: UserPreference;

  private readonly idleEscalationsSubject = new BehaviorSubject<IdleRobotEscalation[]>([]);
  private readonly idleCountSubject = new BehaviorSubject<number>(0);
  private readonly newIdleCountSubject = new BehaviorSubject<number>(0);
  private readonly idleMinutesSubject = new BehaviorSubject<number>(
    RobotEscalationIdleAlertService.DEFAULT_IDLE_MINUTES
  );
  private readonly autoPopupDisabledSubject = new BehaviorSubject<boolean>(false);

  /** Populated only while the idle dialog needs it; the poll does not fetch the list. */
  readonly idleEscalations$ = this.idleEscalationsSubject.asObservable();
  readonly idleCount$ = this.idleCountSubject.asObservable();
  readonly newIdleCount$ = this.newIdleCountSubject.asObservable();
  readonly idleMinutes$ = this.idleMinutesSubject.asObservable();
  readonly autoPopupDisabled$ = this.autoPopupDisabledSubject.asObservable();

  constructor(
    private authService: AuthorizationService,
    private userService: UserService,
    private robotEscalationService: RobotEscalationService,
    private dialog: MatDialog
  ) {
  }

  initialize() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.subscriptions.add(
      this.authService.currentRoles().subscribe((roles) => {
        // Keep bell behavior consistent with hasAccess/admin access semantics in navbar.
        this.isSupervisor = roles.includes('robot_escalation_supervisor') || roles.includes('admin');
        if (!this.isSupervisor) {
          this.stopPolling();
          this.preferenceLoaded = false;
          this.preferenceUserId = null;
          this.userPreference = null;
          this.resetIdleState();
          return;
        }
        this.tryLoadPreference();
      })
    );

    this.subscriptions.add(
      this.authService.getCurrentUser().subscribe((user) => {
        if (this.currentUser?.id !== user.id) {
          this.preferenceLoaded = false;
          this.preferenceUserId = null;
          this.userPreference = null;
        }
        this.currentUser = user;
        this.tryLoadPreference();
      })
    );
  }

  openDialog(source: 'manual' | 'auto' = 'manual') {
    if (!this.isSupervisor) {
      return;
    }
    if (source === 'auto' && this.autoPopupDisabledSubject.value) {
      return;
    }
    if (source === 'auto' && !this.newIdleCountSubject.value) {
      return;
    }
    if (this.dialogRef) {
      return;
    }

    this.dialogRef = this.dialog.open(RobotEscalationIdleDialogComponent, {
      width: '1200px',
      maxWidth: '95vw',
      autoFocus: false
    });
    this.subscriptions.add(
      this.dialogRef.afterClosed().subscribe(() => {
        this.markDialogOpened();
        this.dialogRef = null;
        this.idleEscalationsSubject.next([]);
      })
    );
  }

  openAutoDialogIfNeeded() {
    this.openDialog('auto');
  }

  /**
   * Refresh just the badge counts. This is the polled call and stays deliberately slim.
   */
  refreshIdleCount() {
    if (!this.isSupervisor) {
      return;
    }

    this.robotEscalationService
      .getIdleRobotEscalationCount(this.getLastDialogOpenedAt())
      .subscribe({
        next: (result) => {
          this.idleCountSubject.next(result.count);
          this.newIdleCountSubject.next(result.newCount);
          this.idleMinutesSubject.next(result.idleMinutes);
        },
        error: (err) => {
          console.error('[IdleAlert] idle count error:', err);
        }
      });
  }

  /**
   * Fetch the full idle list. Only called while the idle dialog is open.
   */
  refreshIdleEscalations() {
    if (!this.isSupervisor) {
      return;
    }

    this.robotEscalationService
      .listIdleRobotEscalations(this.idleListParams())
      .subscribe({
        next: (escalations) => {
          // Server orders newest-idle first; re-sort defensively so ordering never depends on it.
          this.idleEscalationsSubject.next(
            [...escalations].sort((a, b) => this.idleSinceValue(b) - this.idleSinceValue(a))
          );
          this.refreshIdleCount();
        },
        error: (err) => {
          console.error('[IdleAlert] idle list error:', err);
        }
      });
  }

  setAutoPopupDisabled(disabled: boolean) {
    this.autoPopupDisabledSubject.next(disabled);
    this.updatePreference(
      RobotEscalationIdleAlertService.PREF_AUTO_POPUP_DISABLED,
      disabled
    );
  }

  markDialogOpened() {
    this.updatePreference(
      RobotEscalationIdleAlertService.PREF_LAST_DIALOG_OPENED_AT,
      new Date().toISOString()
    );
    this.refreshIdleCount();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.subscriptions.unsubscribe();
  }

  private tryLoadPreference() {
    if (
      !this.isSupervisor ||
      !this.currentUser ||
      (this.preferenceLoaded && this.preferenceUserId === this.currentUser.id)
    ) {
      return;
    }

    this.preferenceLoaded = true;
    this.preferenceUserId = this.currentUser.id;
    this.userService.getUser(this.currentUser.id).subscribe({
      next: (user) => {
        this.userService.getOrCreateUserPreference(user).subscribe({
          next: (pref) => {
            this.userPreference = pref;
            this.applyPreference(pref.preferences);

            this.subscriptions.add(
              this.userService.userPreferences(user.id).subscribe({
                next: (updatedPref) => {
                  this.userPreference.preferences = updatedPref.preferences || {};
                  this.applyPreference(this.userPreference.preferences);
                  this.refreshIdleCount();
                },
                error: (err) => {
                  console.error('[IdleAlert] userPreferences error:', err);
                }
              })
            );

            this.startPolling();
          },
          error: (err) => {
            console.error('[IdleAlert] getOrCreateUserPreference error:', err);
          }
        });
      },
      error: (err) => {
        console.error('[IdleAlert] getUser error:', err);
      }
    });
  }

  private applyPreference(preferences: any) {
    // Persisted per-user toggle that suppresses automatic dialog opening.
    const popupDisabled = !!preferences?.[
      RobotEscalationIdleAlertService.PREF_AUTO_POPUP_DISABLED
    ];
    this.autoPopupDisabledSubject.next(popupDisabled);
  }

  private startPolling() {
    if (this.poller) {
      return;
    }

    this.poller = timer(0, RobotEscalationIdleAlertService.POLL_INTERVAL_MS)
      .subscribe({
        next: () => {
          this.refreshIdleCount();
        },
        error: (err) => {
          console.error('[IdleAlert] polling error:', err);
        }
      });
  }

  private stopPolling() {
    if (this.poller) {
      this.poller.unsubscribe();
      this.poller = null;
    }
  }

  private resetIdleState() {
    this.idleEscalationsSubject.next([]);
    this.idleCountSubject.next(0);
    this.newIdleCountSubject.next(0);
  }

  private idleListParams(): any {
    const lastSeenAt = this.getLastDialogOpenedAt();
    return lastSeenAt ? { last_seen_at: lastSeenAt } : {};
  }

  private idleSinceValue(item: IdleRobotEscalation): number {
    return item.idleSince ? item.idleSince.getTime() : 0;
  }

  /**
   * Last time this user opened the idle dialog, as an ISO8601 string, or undefined when never
   * opened. The value is client-owned and passed to the server as a query param.
   */
  private getLastDialogOpenedAt(): string {
    const rawValue = this.userPreference?.preferences?.[
      RobotEscalationIdleAlertService.PREF_LAST_DIALOG_OPENED_AT
    ];
    if (!rawValue) {
      return undefined;
    }

    const parsed = new Date(rawValue);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
  }

  private updatePreference(key: string, value: any) {
    if (!this.userPreference || !this.currentUser) {
      return;
    }

    this.userPreference.preferences = {
      ...(this.userPreference.preferences || {}),
      [key]: value
    };

    this.userService.updateUserPreference(this.userPreference).subscribe();
  }
}
